import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  useTheme as muiUseTheme,
  IconButton,
  InputAdornment,
} from '@mui/material';
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import KeyboardCapslockIcon from '@mui/icons-material/KeyboardCapslock';
import { motion, useReducedMotion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { FantasySportsLogo } from '../components/FantasySportsLogo';
import { useTheme } from '../contexts/ThemeContext';

type LoginStep = 'credentials' | '2fa-setup' | '2fa-verify';

const SportIconRibbon: React.FC<{ reducedMotion: boolean }> = ({ reducedMotion }) => {
  const sports = [
    { Icon: SportsBaseballIcon, color: '#1976d2', delay: 0 },
    { Icon: SportsBasketballIcon, color: '#ff6b35', delay: 0.8 },
    { Icon: SportsFootballIcon, color: '#4caf50', delay: 1.6 },
  ];
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 2,
        mb: 1.5,
        opacity: 0.9,
      }}
    >
      {sports.map(({ Icon, color, delay }, i) => {
        if (reducedMotion) {
          return <Icon key={i} sx={{ fontSize: 24, color }} />;
        }
        return (
          <motion.span
            key={i}
            style={{ display: 'inline-flex' }}
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ duration: 2.4, delay, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Icon sx={{ fontSize: 24, color, filter: `drop-shadow(0 0 8px ${color}66)` }} />
          </motion.span>
        );
      })}
    </Box>
  );
};

const LoginPage: React.FC = () => {
  const { login, loginWith2FA } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const muiTheme = muiUseTheme();
  const isXs = useMediaQuery(muiTheme.breakpoints.down('sm'));
  // Tight phones can't fit the default 200px QR with padding; clamp to 160 on xs.
  const qrSize = isXs ? 160 : 200;
  const reducedMotion = useReducedMotion() ?? false;

  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA setup state
  const [setupToken, setSetupToken] = useState('');
  const [qrCodeUri, setQrCodeUri] = useState('');
  const [totpSecret, setTotpSecret] = useState('');

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.mustSetup2FA && result.setupToken) {
        setSetupToken(result.setupToken);
        // Fetch QR code
        const setup = await authService.setup2FA(result.setupToken);
        setQrCodeUri(setup.qrCodeUri);
        setTotpSecret(setup.secret);
        setStep('2fa-setup');
      } else if (result.requiresTwoFactor) {
        setStep('2fa-verify');
      } else if (result.mustResetPassword) {
        navigate('/settings/security?reset=1', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.confirm2FA(setupToken, totpCode);
      if (response.accessToken && response.refreshToken) {
        authService.saveTokens(response.accessToken, response.refreshToken);
        if (response.mustResetPassword) {
          window.location.href = '/settings/security?reset=1';
        } else {
          window.location.href = from;
        }
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr.response?.data?.message || 'Invalid code. Please try again.');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await loginWith2FA(email, password, totpCode);
      if (result.mustResetPassword) {
        navigate('/settings/security?reset=1', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr.response?.data?.message || 'Invalid 2FA code');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const activeStep = step === 'credentials' ? 0 : step === '2fa-setup' ? 1 : 1;

  // Glass card styling — semi-transparent panel with backdrop blur. The double-
  // background trick (padding-box solid + border-box gradient) draws a 1px
  // sport-color sweep on the edge without losing the translucent interior.
  const cardBg = isDarkMode ? 'rgba(20, 22, 30, 0.72)' : 'rgba(255, 255, 255, 0.78)';
  const borderAlpha = isDarkMode ? 0.25 : 0.18;
  const glassSx = {
    background: `linear-gradient(${cardBg}, ${cardBg}) padding-box, linear-gradient(135deg, rgba(25,118,210,${borderAlpha}) 0%, rgba(255,107,53,${borderAlpha * 0.85}) 50%, rgba(76,175,80,${borderAlpha}) 100%) border-box`,
    border: '1px solid transparent',
    boxShadow: isDarkMode
      ? '0 20px 60px rgba(0, 0, 0, 0.6), 0 0 80px rgba(25, 118, 210, 0.15)'
      : '0 20px 60px rgba(0, 0, 0, 0.12)',
  };

  // Primary CTA — sport-blue gradient with a soft drop-shadow that lifts on hover.
  const primaryButtonSx = {
    py: 1.5,
    fontWeight: 600,
    background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #42a5f5 100%)',
    boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
    transition: 'box-shadow 0.25s ease, transform 0.25s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #1976d2 0%, #1e88e5 50%, #64b5f6 100%)',
      boxShadow: '0 6px 22px rgba(25, 118, 210, 0.55)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 3px 10px rgba(25, 118, 210, 0.4)',
    },
    '&.Mui-disabled': {
      background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      boxShadow: 'none',
    },
  };

  // TextField — bright sport-blue focus ring with a soft outer glow on focus.
  // Also override Chrome's `-webkit-autofill` so saved-credential clicks don't
  // paint a muddy gray over the dark glass card.
  const autofillBg = isDarkMode ? '#13151c' : '#ffffff';
  const autofillText = isDarkMode ? '#e0e3eb' : '#1a1a1a';
  const accentTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      transition: 'box-shadow 0.2s ease',
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${isDarkMode ? 'rgba(66,165,245,0.22)' : 'rgba(25,118,210,0.18)'}`,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: isDarkMode ? '#42a5f5' : '#1976d2',
        borderWidth: 1.5,
      },
    },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
      {
        WebkitBoxShadow: `0 0 0 100px ${autofillBg} inset`,
        WebkitTextFillColor: autofillText,
        caretColor: autofillText,
        // Long transition delays the browser repainting the autofill bg long enough
        // that it never visibly flashes during normal interaction.
        transition: 'background-color 600000s ease-in-out 0s, color 600000s ease-in-out 0s',
      },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        bgcolor: isDarkMode ? '#07080c' : '#f5f7fa',
        p: 2,
      }}
    >
      <motion.div
        initial={reducedMotion ? false : { opacity: 0 }}
        animate={reducedMotion ? undefined : { opacity: 1 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: 440 }}
      >
        <Card
          sx={{
            width: '100%',
            borderRadius: 3,
            backdropFilter: 'blur(20px) saturate(140%)',
            WebkitBackdropFilter: 'blur(20px) saturate(140%)',
            ...glassSx,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Sport icon ribbon — clarifies what the app is at a glance. */}
            <SportIconRibbon reducedMotion={reducedMotion} />

            {/* Logo + wordmark with entry scale + glow pulse. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 3 }}>
              <motion.div
                initial={reducedMotion ? false : { scale: 0.85, opacity: 0 }}
                animate={reducedMotion ? undefined : { scale: 1, opacity: 1 }}
                transition={{
                  scale: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
                  opacity: { duration: 0.4 },
                }}
                style={{ display: 'inline-flex' }}
              >
                <FantasySportsLogo width={32} height={32} onDark={isDarkMode} />
              </motion.div>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '1.1rem',
                  letterSpacing: '0.06em',
                }}
              >
                Fantasy Sports
              </Typography>
            </Box>

            {step !== 'credentials' && (
              <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                <Step completed><StepLabel>Sign In</StepLabel></Step>
                <Step><StepLabel>{step === '2fa-setup' ? 'Setup 2FA' : 'Verify 2FA'}</StepLabel></Step>
              </Stepper>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Step 1: Credentials */}
            {step === 'credentials' && (
              <form onSubmit={handleCredentialsSubmit}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 2, ...accentTextFieldSx }}
                  autoFocus
                  autoComplete="email"
                />
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => setCapsLockOn(e.getModifierState('CapsLock'))}
                  onKeyUp={(e) => setCapsLockOn(e.getModifierState('CapsLock'))}
                  onBlur={() => setCapsLockOn(false)}
                  sx={{ mb: capsLockOn ? 1 : 3, ...accentTextFieldSx }}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {capsLockOn && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mb: 2,
                      color: 'warning.main',
                    }}
                  >
                    <KeyboardCapslockIcon fontSize="small" />
                    <Typography variant="caption">Caps Lock is on</Typography>
                  </Box>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={primaryButtonSx}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>
              </form>
            )}

            {/* Step 2a: 2FA Setup (first time) */}
            {step === '2fa-setup' && (
              <form onSubmit={handleSetupConfirm}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, p: 2, bgcolor: 'white', borderRadius: 2 }}>
                  <QRCodeSVG value={qrCodeUri} size={qrSize} />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2, wordBreak: 'break-all' }}>
                  Manual entry: {totpSecret}
                </Typography>

                <TextField
                  label="6-digit code"
                  fullWidth
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  sx={{ mb: 3, ...accentTextFieldSx }}
                  autoFocus
                  autoComplete="one-time-code"
                  inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                  placeholder="000000"
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || totpCode.length !== 6}
                  sx={primaryButtonSx}
                >
                  {loading ? <CircularProgress size={24} /> : 'Verify & Enable 2FA'}
                </Button>
              </form>
            )}

            {/* Step 2b: 2FA Verify (returning user) */}
            {step === '2fa-verify' && (
              <form onSubmit={handle2FAVerify}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  Enter the 6-digit code from your authenticator app
                </Typography>

                <TextField
                  label="2FA Code"
                  fullWidth
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  sx={{ mb: 3, ...accentTextFieldSx }}
                  autoFocus
                  autoComplete="one-time-code"
                  inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                  placeholder="000000"
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || totpCode.length !== 6}
                  sx={primaryButtonSx}
                >
                  {loading ? <CircularProgress size={24} /> : 'Verify'}
                </Button>

                <Button
                  fullWidth
                  sx={{ mt: 1 }}
                  onClick={() => {
                    setStep('credentials');
                    setTotpCode('');
                    setError('');
                  }}
                >
                  Back to Sign In
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default LoginPage;
